import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import {
  BehaviorValidationStatus,
  CaseBehaviour,
  QuestionnaireGatingStatus,
} from '../../../insights.interface';

export interface DraftBehaviorSummary {
  behaviorId: number;
  behaviorName: string;
  pendingItemNames: string[];
}

@Component({
  selector: 'app-case-plan-status-banner',
  standalone: true,
  imports: [CommonModule, AngularSvgIconModule],
  templateUrl: './case-plan-status-banner.component.html',
  styleUrl: './case-plan-status-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CasePlanStatusBannerComponent {
  @Input() status: QuestionnaireGatingStatus | null | undefined = null;
  @Input() behaviorList: BehaviorValidationStatus[] | null | undefined = null;
  @Input() caseBehaviors: CaseBehaviour[] | null | undefined = null;
  @Input() templateName: string | null | undefined = null;

  get hasPendingItems(): boolean {
    return !!(
      this.status?.hasPendingHardStop || this.status?.hasPendingRequired
    );
  }

  get isQuestionnaireValid(): boolean {
    return !this.hasPendingItems;
  }

  get hardStopItemNames(): string[] {
    return (this.status?.pendingHardStopItems ?? []).map(item => item.name);
  }

  get requiredItemNames(): string[] {
    return (this.status?.pendingRequiredItems ?? []).map(item => item.name);
  }

  // Every behavior still in Draft (Required/Hard-Stop items outstanding),
  // paired with the pending item names from the matching behaviorList entry.
  get draftBehaviors(): DraftBehaviorSummary[] {
    if (!this.isQuestionnaireValid) {
      return [];
    }
    return (this.caseBehaviors ?? [])
      .filter(behavior => behavior.draft)
      .map(behavior => {
        const validationStatus = this.behaviorList?.find(
          item => item.behaviorId === behavior.id
        );
        return { behavior, validationStatus };
      })
      .filter(
        ({ validationStatus }) =>
          validationStatus?.hasPendingHardStop ||
          validationStatus?.hasPendingRequired
      )
      .map(({ behavior, validationStatus }) => ({
        behaviorId: behavior.id,
        behaviorName:
          validationStatus?.behaviorName || behavior.behaviorCategoryName,
        pendingItemNames: [
          ...(validationStatus?.pendingHardStopItems ?? []),
          ...(validationStatus?.pendingRequiredItems ?? []),
        ].map(item => item.name),
      }));
  }

  trackByBehaviorId(_index: number, behavior: DraftBehaviorSummary): number {
    return behavior.behaviorId;
  }
}
